import React from "react";
import EnhancedCard from "common/components/EnhancedCard";
import EnhancedInput from "common/components/EnhancedInput";
import EnhancedSelect from "common/components/EnhancedSelect";
import CompositeDateTimePickers from "common/components/CompositeDateTimePickers";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import get from "lodash/get";
import ImageUploader from "core/ImageUploader";
import has from "lodash/has";
import * as Constants from "common/constants";
import CustomValuesInput from "./CustomValuesInput";
import CustomValuesField from "../OfferDetails/CustomValuesField";
import Field from "../OfferDetails/Field";
import * as datetime from "common/utils/datetime";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Divider,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import moment from "moment";
import OfferPreview from "../OfferPreview";
import QuantityInput from "./QuantityInput";
import { useTranslation } from "react-i18next";

const counter = (values, errors, touched) => {
  if (touched.short_description && get(errors, "short_description")) {
    return get(errors, "short_description");
  }
  const limit = Constants.SHORT_DESCRIPTION_MAX_LENGTH;
  const remaining = get(values, "short_description.length", 0);
  return `(${remaining}/${limit})`;
};

// Update the targetOptions function
const targetOptions = (t, eventTargets = [], groupTargets = []) =>
  [
    // {
    //   id: "community",
    //   name: t("offer_create.view_option_neighborhood"),
    // },
    {
      id: "building",
      name: t("offer_create.view_option_building"),
    },
    ...(groupTargets.length > 0
      ? groupTargets.map((target) => ({
          id: target.value,
          name: `${target.label}`,
        }))
      : []),
    ...(eventTargets.length > 0
      ? eventTargets.map((target) => ({
          id: target.value,
          name: `${target.label}`,
        }))
      : []),
  ].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, {
      sensitivity: "base",
    }),
  );

const OfferForm = ({
  classes,
  values,
  errors,
  handleChange,
  setFieldValue,
  setFieldTouched,
  touched,
  timezone,
  eventTargets,
  groupTargets,
}) => {
  const { t } = useTranslation();
  return (
    <Grid container>
      <Grid size={{ xs: 6 }}>
        <Grid container direction="column" spacing={2}>
          <Grid>
            <EnhancedCard title={t("offer_create.basic_title")}>
              <div className={classes.formGroup}>
                <EnhancedInput
                  id="short_description"
                  type="text"
                  name="short_description"
                  label={t("offer_create.basic_called_label")}
                  fullWidth
                  error={
                    touched.short_description &&
                    has(errors, "short_description")
                  }
                  helperText={
                    <>
                      <div>{counter(values, errors, touched)}</div>
                      <div>
                        {t("offer_create.basic_called_hint", {
                          maxChar: Constants.SHORT_DESCRIPTION_MAX_LENGTH,
                        })}
                      </div>
                    </>
                  }
                  maxLength={Constants.SHORT_DESCRIPTION_MAX_LENGTH}
                  onChange={handleChange}
                  value={values.short_description}
                />
              </div>
              {values.status === "published" ? (
                <div className={classes.formGroup}>
                  <CustomValuesField
                    template={get(values, "offer_template")}
                    type={get(values, "offer_template.id")}
                    values={values.custom_values}
                  />
                  <Divider />
                </div>
              ) : (
                <div className={classes.formGroup}>
                  <CustomValuesInput
                    type={get(values, "offer_template.id")}
                    values={values}
                    touched={touched}
                    errors={errors}
                    handleChange={(value) =>
                      setFieldValue("custom_values", value)
                    }
                  />
                </div>
              )}
              <div className={classes.formGroup}>
                <EnhancedInput
                  id="long_description"
                  name="long_description"
                  type="text"
                  label={t("offer_create.basic_highlight_title")}
                  caption={
                    <FormHelperText className={classes.descText}>
                      {t("offer_create.basic_highlight_subtitle")}
                    </FormHelperText>
                  }
                  rows="4"
                  fullWidth
                  multiline
                  error={
                    touched.long_description && has(errors, "long_description")
                  }
                  helperText={
                    touched.long_description && get(errors, "long_description")
                  }
                  onChange={handleChange}
                  value={values.long_description}
                />
              </div>
            </EnhancedCard>
          </Grid>
          <Grid>
            <EnhancedCard title={t("offer_create.design_title")}>
              <div className={classes.formGroup}>
                <ImageUploader
                  buttonId="hero_image_uploader_btn"
                  inputId="hero_image_input"
                  label={
                    <FormHelperText className={classes.uploadImageHintText}>
                      <span>{t("offer_create.design_hint")}</span>
                    </FormHelperText>
                  }
                  captionStyle={{
                    whiteSpace: "pre-wrap",
                    color: "rgba(0, 0, 0, 0.54)",
                  }}
                  caption={
                    <FormHelperText className={classes.uploadImageDescText}>
                      <span>{t("offer_create.design_desc")}</span>
                      <span>
                        <span
                          style={{
                            textDecoration: "underline",
                          }}
                        >
                          {t("offer_create.design_desc_note_title")}
                        </span>
                      </span>
                      <span>{t("offer_create.design_desc_note_text")}</span>
                    </FormHelperText>
                  }
                  id="hero"
                  name="hero"
                  initialImageUrl={values.hero_url || ""}
                  touched={has(touched, "hero")}
                  error={touched.target && has(errors, "hero_url")}
                  onChange={(file, opts) => {
                    setFieldValue("imagePreviewUrl", opts.imagePreviewUrl);
                    setFieldValue("hero_url", opts.imagePreviewUrl);
                    setFieldTouched("hero", true);
                    setFieldValue("hero", file, false);
                  }}
                />
              </div>
            </EnhancedCard>
          </Grid>
          <Grid xs>
            <EnhancedCard title={t("offer_create.timing_title")}>
              <Grid container direction="column">
                <Grid size={{ xs: 8 }} className={classes.formGroup}>
                  <FormControl className={classes.formControl} fullWidth>
                    {/* <Typography className={classes.label}>
                      {t("offer_create.timing_label")}
                    </Typography> */}
                    <Grid container direction="column" spacing={2}>
                      <Grid xs>
                        {values.status === "published" ? (
                          <div>
                            <Field
                              title={t("offer_create.timing_start_label")}
                              value={datetime.full(values.start_at, timezone)}
                            />
                            <Divider />
                          </div>
                        ) : (
                          <CompositeDateTimePickers
                            labelId="start-datetime-label"
                            dateInputId="start-date-input"
                            timeInputId="start-time-input"
                            label={t("offer_create.timing_start_label")}
                            name="start_at"
                            disableTime={values.fullDay}
                            value={values.start_at}
                            error={has(errors, "start_at")}
                            timezone={timezone}
                            handleChange={(value) => {
                              setFieldValue("start_at", value);
                              setFieldValue("fullDay", false);
                            }}
                          />
                        )}
                      </Grid>
                      <Grid xs>
                        <CompositeDateTimePickers
                          labelId="end-datetime-label"
                          dateInputId="end-date-input"
                          timeInputId="end-time-input"
                          label={t("offer_create.timing_end_label")}
                          name="end_at"
                          disableTime={values.fullDay}
                          value={values.end_at}
                          error={touched.end_at && has(errors, "end_at")}
                          helperText={touched.end_at && get(errors, "end_at")}
                          timezone={timezone}
                          handleChange={(value) => {
                            setFieldValue("end_at", value);
                            setFieldValue("fullDay", false);
                          }}
                        />
                      </Grid>
                      {values.status !== "published" && (
                        <Grid xs>
                          <FormControlLabel
                            id="fullDay"
                            name="fullDay"
                            control={
                              <Checkbox
                                checked={values.fullDay}
                                disabled={
                                  !(
                                    moment(values.start_at).isValid() &&
                                    moment(values.end_at).isValid()
                                  )
                                }
                                onChange={(event, checked) => {
                                  setFieldValue("fullDay", checked);
                                  setFieldValue(
                                    "start_at",
                                    moment
                                      .tz(
                                        moment(values.start_at)
                                          .tz(timezone)
                                          .startOf("day"),
                                        timezone,
                                      )
                                      .utc()
                                      .toDate(),
                                  );
                                  setFieldValue(
                                    "end_at",
                                    moment
                                      .tz(
                                        moment(values.end_at)
                                          .tz(timezone)
                                          .endOf("day"),
                                        timezone,
                                      )
                                      .utc()
                                      .toDate(),
                                  );
                                }}
                              />
                            }
                            label={t("offer_create.timing_checkbox")}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 8 }} className={classes.formGroup}>
                  <EnhancedSelect
                    id="target"
                    name="target"
                    error={touched.target && has(errors, "target")}
                    value={values.target}
                    helperText={
                      touched.target && has(errors, "target")
                        ? errors.target
                        : t("offer_create.view_hint")
                    }
                    onChange={handleChange}
                    disabled={values.status === "published"}
                    label={t("offer_create.view_label")}
                  >
                    {targetOptions(t, eventTargets, groupTargets).map(
                      (option) => (
                        <MenuItem value={option.id} key={option.id}>
                          {option.name}
                        </MenuItem>
                      ),
                    )}
                  </EnhancedSelect>
                </Grid>
                <Grid size={{ xs: 8 }} className={classes.formGroup}>
                  <EnhancedInput
                    id="quantity"
                    type="text"
                    label={t("offer_create.quantity_title")}
                    caption={t("offer_create.quantity_subtitle")}
                    fullWidth
                    inputComponent={QuantityInput}
                    error={touched.quantity && has(errors, "quantity")}
                    helperText={touched.quantity && get(errors, "quantity")}
                    onChange={(value) => setFieldValue("quantity", value)}
                    value={values.quantity}
                  />
                </Grid>
              </Grid>
            </EnhancedCard>
          </Grid>
          <Grid xs>
            <EnhancedCard title={t("offer_create.terms_title")}>
              <EnhancedInput
                id="legal"
                name="legal"
                type="text"
                label={t("offer_create.terms_desc")}
                caption={t("offer_create.terms_hint")}
                fullWidth
                multiline
                rows="6"
                error={touched.legal && has(errors, "legal")}
                helperText={touched.legal && get(errors, "legal")}
                onChange={handleChange}
                value={values.legal}
                disabled={values.defaultTerms}
              />
              <FormControlLabel
                id="defaultTerms"
                name="defaultTerms"
                control={
                  <Checkbox
                    checked={values.defaultTerms}
                    onChange={(event, checked) => {
                      setFieldValue("defaultTerms", checked);
                      setFieldValue(
                        "legal",
                        checked ? values.offer_template.legal : "",
                      );
                    }}
                  />
                }
                label={t("offer_create.use_sample_checkbox")}
              />
            </EnhancedCard>
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 4 }}>
        <OfferPreview
          template={values.offer_template}
          customValues={values.custom_values}
          shortDescription={values.short_description}
          imagePreviewUrl={
            values.imagePreviewUrl?.length
              ? values.imagePreviewUrl
              : values.hero_url || "https://www.placehold.it/345x160"
          }
        />
      </Grid>
    </Grid>
  );
};

const styles = (theme) => ({
  formControl: {
    flex: 1,
  },
  row: {
    marginTop: theme.spacing(2),
  },
  card: {
    maxWidth: 345,
  },
  cardMedia: {
    height: 160,
  },
  title: {
    fontWeight: 600,
  },
  addCircle: {
    color: theme.palette.grey[700],
    fontSize: 16,
    margin: 0,
  },
  cardContent: {
    padding: theme.spacing(1),
  },
  formGroup: {
    marginBottom: theme.spacing(1),
    width: "80%",
  },
  cardActions: {
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
  },
  uploadImageHintText: {
    color: "#000000de",
    fontWeight: "bold",
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    fontSize: "1rem",
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
  },
  uploadImageDescText: {
    color: "#000000de",
    fontWeight: "400",
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    fontSize: "0.75rem",
    lineHeight: "16px",
    letterSpacing: "0.03333em",
  },
  descText: {
    color: "#000000de",
    fontWeight: "400",
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    fontSize: "0.75rem",
    lineHeight: "16px",
    letterSpacing: "0.03333em",
  },
  captionText: {
    fontSize: "0.75rem",
    lineHeight: "16px",
  },
  label: {
    fontWeight: "bold",
  },
});

OfferForm.propTypes = {
  classes: PropTypes.object.isRequired,
  values: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  status: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  eventTargets: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  groupTargets: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

export default withStyles(styles)(OfferForm);
